/**
 * Enhanced Planning Poker - Agile estimation tool with dollar estimation support
 * Supports both hours-based estimation (for tasks) and dollar-based estimation (for contractor bids)
 * Features private voting mode for contractor cost estimation
 */

import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { saveState, addCommentToEntity } from '../store';
import { Task, PlanningPokerSession, PlanningPokerVote, Comment, Notification } from '../types';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { 
  Users, Eye, EyeOff, CheckCircle, Clock, TrendingUp, Coffee, HelpCircle, 
  Zap, DollarSign, Lock, Unlock, Settings as SettingsIcon, UserCheck 
} from 'lucide-react';
import { toast } from 'sonner';
import { useApp } from '../providers/AppProvider';
import { generateId } from '../utils/idGenerator';
import { filterVisibleTasksForUser } from '../utils/projectAccess';
import {
  WIDGET_ALERT_PURPLE_CLASS,
  WIDGET_ALERT_PURPLE_TEXT_CLASS,
  WIDGET_ALERT_WARNING_CLASS,
  WIDGET_ALERT_WARNING_TEXT_CLASS,
} from '../constants/widgetTheme';

const POKER_VALUES_HOURS = ['0', '1', '2', '3', '5', '8', '13', '21', '?', '☕'];
const POKER_VALUES_DOLLARS = ['100', '500', '1000', '2500', '5000', '10000', '25000', '50000', '?', 'Custom'];
const POKER_INSET_PANEL_CLASS = 'rounded-lg border border-border bg-background';

export default function PlanningPoker() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const taskId = searchParams.get('taskId');

  const [selectedTask, setSelectedTask] = useState<Task | null>(null);
  const [myVote, setMyVote] = useState<string | null>(null);
  const [customAmount, setCustomAmount] = useState('');
  const [showCreateSession, setShowCreateSession] = useState(false);
  const [selectedParticipants, setSelectedParticipants] = useState<string[]>([]);
  
  // Session creation settings
  const [estimationMode, setEstimationMode] = useState<'hours' | 'dollars'>('hours');
  const [privateVoting, setPrivateVoting] = useState(false);
  const [allowCustomValues, setAllowCustomValues] = useState(false);
  const [sessionDescription, setSessionDescription] = useState('');
  const { state, setState } = useApp();
  const allTasks = state?.tasks ?? [];
  const projectMap = new Map((state?.projects ?? []).map((project) => [project.id, project]));
  const tasks = filterVisibleTasksForUser(
    state?.currentUser,
    allTasks,
    (task) => projectMap.get(task.projectId) || null
  );
  
  
  const currentUser = state?.currentUser ?? null;
  const orgUsers = state?.users?.filter(
    u => u.organizationId === state?.currentOrganization?.id
  ) ?? [];
  const isCurrentUserDeveloper = currentUser?.role === 'developer';
  const participantCandidates = orgUsers.filter((user) =>
    estimationMode === 'dollars' ? user.role !== 'developer' : true
  );
  const participantCandidateIds = participantCandidates.map((user) => user.id);

  // Load task if taskId is provided
  useEffect(() => {
    if (taskId) {
      const task = tasks.find(t => t.id === taskId);
      if (task) {
        setSelectedTask(task);
        if (
          !isCurrentUserDeveloper &&
          (task.category?.toLowerCase().includes('construction') ||
            task.category?.toLowerCase().includes('contractor'))
        ) {
          setEstimationMode('dollars');
          setPrivateVoting(true);
          setAllowCustomValues(true);
        }
      }
    }
  }, [isCurrentUserDeveloper, taskId, tasks]);

  // Get tasks with active planning poker sessions
  const tasksWithActiveSessions = tasks.filter(
    t => t.organizationId === state?.currentOrganization?.id &&
         t.planningPokerSession &&
         t.planningPokerSession.status === 'active'
  );

  // Get tasks ready for estimation (no session yet)
  const tasksReadyForEstimation = tasks.filter(
    t => t.organizationId === state?.currentOrganization?.id &&
         t.status === 'Not Started' &&
         !t.planningPokerSession
  ).slice(0, 20);

  if (!state) {
    return <div className="p-8 text-center text-muted-foreground">Loading planning poker...</div>;
  }

  const createSession = (task: Task) => {
    if (!currentUser) return;

    const participantCandidateSet = new Set(participantCandidateIds);

    const participants = selectedParticipants.length > 0 
      ? selectedParticipants.filter((participantId) => participantCandidateSet.has(participantId))
      : participantCandidateIds;

    if (participants.length === 0) {
      toast.error('No eligible participants selected for this estimation mode.');
      return;
    }

    const session: PlanningPokerSession = {
      id: generateId('poker', task.id),
      taskId: task.id,
      createdAt: new Date().toISOString(),
      createdBy: currentUser.id,
      status: 'active',
      votes: [],
      revealed: false,
      participantIds: participants,
      estimationMode,
      privateVoting,
      allowCustomValues,
      description: sessionDescription || undefined,
    };

    const updatedTasks = state.tasks.map(t =>
      t.id === task.id ? { ...t, planningPokerSession: session } : t
    );

    // Send notifications to all participants
    const notifications: Notification[] = participants.map(userId => ({
      id: generateId('notif', task.id, userId),
      organizationId: state.currentOrganization!.id,
      userId,
      type: 'planning_poker' as const,
      priority: 'medium',
      title: `Planning Poker: ${task.title}`,
      message: `${currentUser.name} invited you to estimate ${estimationMode === 'dollars' ? 'cost' : 'time'} for this task. ${sessionDescription || ''}`,
      entityType: 'task',
      entityId: task.id,
      actionUrl: `/app/planning-poker?taskId=${task.id}`,
      read: false,
      createdAt: new Date().toISOString(),
    }));

    const newState = { 
      ...state, 
      tasks: updatedTasks,
      notifications: [...state.notifications, ...notifications],
    };
    setState(newState);
    saveState(newState);
    setSelectedTask({ ...task, planningPokerSession: session });
    setShowCreateSession(false);
    setSessionDescription('');
    setSelectedParticipants([]);
    toast.success(`${estimationMode === 'dollars' ? 'Cost estimation' : 'Time estimation'} session created and ${participants.length} participants notified`);
  };

  const castVote = (task: Task, vote: string) => {
    if (!currentUser || !task.planningPokerSession) return;

    const session = task.planningPokerSession;

    if (session.estimationMode === 'dollars' && currentUser.role === 'developer') {
      toast.error('Developers cannot view or submit cost estimates.');
      return;
    }
    
    // Validate custom dollar amount
    let finalVote = vote;
    if (vote === 'Custom' && session.estimationMode === 'dollars') {
      const amount = parseFloat(customAmount);
      if (isNaN(amount) || amount <= 0) {
        toast.error('Please enter a valid dollar amount');
        return;
      }
      finalVote = customAmount;
      setCustomAmount('');
    }
    
    // Remove any existing vote from this user
    const filteredVotes = session.votes.filter(v => v.userId !== currentUser.id);
    
    // Add new vote
    const newVote: PlanningPokerVote = {
      userId: currentUser.id,
      userName: currentUser.name,
      vote: finalVote,
      votedAt: new Date().toISOString(),
      isContractor: currentUser.role === 'external_partner',
    };

    const updatedSession: PlanningPokerSession = {
      ...session,
      votes: [...filteredVotes, newVote],
    };

    const updatedTasks = state.tasks.map(t =>
      t.id === task.id ? { ...t, planningPokerSession: updatedSession } : t
    );

    const newState = { ...state, tasks: updatedTasks };
    setState(newState);
    saveState(newState);
    setMyVote(finalVote);

    if (selectedTask?.id === task.id) {
      setSelectedTask({ ...task, planningPokerSession: updatedSession });
    }

    toast.success('Vote submitted');
  };

  const revealVotes = (task: Task) => {
    if (!currentUser || !task.planningPokerSession) return;

    const session = task.planningPokerSession;
    const updatedSession: PlanningPokerSession = {
      ...session,
      revealed: true,
      revealedAt: new Date().toISOString(),
      revealedBy: currentUser.id,
      status: 'revealed',
    };

    const updatedTasks = state.tasks.map(t =>
      t.id === task.id ? { ...t, planningPokerSession: updatedSession } : t
    );

    const newState = { ...state, tasks: updatedTasks };
    setState(newState);
    saveState(newState);

    if (selectedTask?.id === task.id) {
      setSelectedTask({ ...task, planningPokerSession: updatedSession });
    }

    toast.success('Votes revealed!');
  };

  const finalizeEstimate = async (task: Task, estimate: number) => {
    if (!currentUser || !task.planningPokerSession) return;

    const session = task.planningPokerSession;
    const consensus = getConsensus(session.votes);
    const average = calculateAverage(session.votes);
    const isDollarMode = session.estimationMode === 'dollars';
    
    // Create a summary comment with all results
    const votesSummary = session.votes
      .map(v => {
        if (isDollarMode) {
          return `• ${v.userName}: Submitted${v.isContractor ? ' (Contractor bid hidden)' : ''}`;
        }

        return `• ${v.userName}: ${v.vote}${v.isContractor ? ' (Contractor)' : ''}`;
      })
      .join('\n');

    const resultContent = isDollarMode
      ? `**Planning Poker Results** 🎯\n\n**Mode:** Cost Estimation 💰\n**Final Cost:** Restricted (admin visibility only)\n\n**Votes:**\n${votesSummary}\n\n**Statistics:**\n• Bid values: Restricted (admin visibility only)\n• Participants: ${session.votes.length}/${session.participantIds.length}${session.description ? `\n**Context:** ${session.description}` : ''}`
      : `**Planning Poker Results** 🎯\n\n**Mode:** Time Estimation ⏱️\n**Final Estimate:** ${estimate.toLocaleString()} hours\n\n**Votes:**\n${votesSummary}\n\n**Statistics:**\n• Average: ${average.toLocaleString()}h\n• Consensus: ${consensus.hasConsensus ? `✅ ${consensus.value}h` : '❌ No consensus'}\n• Participants: ${session.votes.length}/${session.participantIds.length}${session.description ? `\n**Context:** ${session.description}` : ''}`;
    
    const resultComment: Comment = {
      id: generateId('comment', task.id),
      content: resultContent,
      createdBy: currentUser.id,
      createdByName: currentUser.name,
      createdAt: new Date().toISOString(),
    };

    // Add comment to task
    await addCommentToEntity('task', task.id, resultComment);
    
    const updatedSession: PlanningPokerSession = {
      ...session,
      finalEstimate: estimate,
      status: 'completed',
    };

    // Update task with estimate
    const taskUpdate = session.estimationMode === 'dollars'
      ? { planningPokerSession: updatedSession, cost: estimate }
      : { planningPokerSession: updatedSession, estimatedHours: estimate };

    setState(prev => {
      if (!prev) return prev;

      const updatedTasks = prev.tasks.map(t =>
        t.id === task.id ? { ...t, ...taskUpdate } : t
      );

      return { ...prev, tasks: updatedTasks };
    });
    setSelectedTask(null);
    setMyVote(null);

    toast.success(`Final ${session.estimationMode === 'dollars' ? 'cost' : 'estimate'} saved and posted as comment: ${session.estimationMode === 'dollars' ? '$' : ''}${estimate}${session.estimationMode === 'hours' ? 'h' : ''}`);
  };

  const stopSession = (task: Task) => {
    if (!confirm('Stop this estimation session? All votes will be lost.')) return;
    
    const updatedTasks = state.tasks.map(t =>
      t.id === task.id ? { ...t, planningPokerSession: undefined } : t
    );

    const newState = { ...state, tasks: updatedTasks };
    setState(newState);
    saveState(newState);
    setSelectedTask(null);
    setMyVote(null);
    toast.success('Session stopped');
  };

  const restartVoting = (task: Task) => {
    if (!currentUser || !task.planningPokerSession) return;
    if (!confirm('Restart voting? This will clear all current votes.')) return;

    const session = task.planningPokerSession;
    const updatedSession: PlanningPokerSession = {
      ...session,
      votes: [],
      revealed: false,
      revealedAt: undefined,
      revealedBy: undefined,
      status: 'active',
    };

    const updatedTasks = state.tasks.map(t =>
      t.id === task.id ? { ...t, planningPokerSession: updatedSession } : t
    );

    const newState = { ...state, tasks: updatedTasks };
    setState(newState);
    saveState(newState);
    setMyVote(null);

    if (selectedTask?.id === task.id) {
      setSelectedTask({ ...task, planningPokerSession: updatedSession });
    }

    toast.success('Voting restarted');
  };

  const redoVoting = (task: Task) => {
    if (!currentUser || !task.planningPokerSession) return;
    if (!confirm('Redo voting? This will hide all votes and allow everyone to vote again.')) return;

    const session = task.planningPokerSession;
    const updatedSession: PlanningPokerSession = {
      ...session,
      revealed: false,
      revealedAt: undefined,
      revealedBy: undefined,
      status: 'active',
    };

    const updatedTasks = state.tasks.map(t =>
      t.id === task.id ? { ...t, planningPokerSession: updatedSession } : t
    );

    const newState = { ...state, tasks: updatedTasks };
    setState(newState);
    saveState(newState);

    if (selectedTask?.id === task.id) {
      setSelectedTask({ ...task, planningPokerSession: updatedSession });
    }

    toast.success('Votes hidden - ready to vote again');
  };

  const getVoteIcon = (value: string, mode: 'hours' | 'dollars') => {
    if (value === '?') return <HelpCircle className="h-6 w-6" />;
    if (value === '☕') return <Coffee className="h-6 w-6" />;
    if (mode === 'dollars' && value !== 'Custom') {
      const num = parseFloat(value);
      if (!isNaN(num)) {
        return <span className="text-xl font-bold">${num.toLocaleString()}</span>;
      }
    }
    if (value === 'Custom') {
      return <span className="text-sm font-bold">Custom</span>;
    }
    return <span className="text-2xl font-bold">{value}</span>;
  };

  const calculateAverage = (votes: PlanningPokerVote[]): number => {
    const numericVotes = votes
      .map(v => v.vote)
      .filter(v => v !== '?' && v !== '☕')
      .map(v => parseFloat(v))
      .filter(v => !isNaN(v));
    
    if (numericVotes.length === 0) return 0;
    return Math.round(numericVotes.reduce((a, b) => a + b, 0) / numericVotes.length);
  };

  const getConsensus = (votes: PlanningPokerVote[]): { hasConsensus: boolean; value?: string } => {
    const numericVotes = votes.filter(v => v.vote !== '?' && v.vote !== '☕');
    if (numericVotes.length === 0) return { hasConsensus: false };

    const voteCounts = numericVotes.reduce((acc, v) => {
      acc[v.vote] = (acc[v.vote] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    const mostCommon = Object.entries(voteCounts).sort((a, b) => b[1] - a[1])[0];
    const hasConsensus = mostCommon[1] / numericVotes.length >= 0.7;

    return { hasConsensus, value: hasConsensus ? mostCommon[0] : undefined };
  };

  const canSeeOtherVotes = (session: PlanningPokerSession): boolean => {
    if (session.privateVoting && !session.revealed && currentUser?.role === 'external_partner') {
      return false;
    }
    if (currentUser?.role === 'admin' || currentUser?.role === 'stakeholder' || currentUser?.role === 'investor') {
      return true;
    }
    return session.revealed;
  };

  const canViewVoteValue = (session: PlanningPokerSession, vote: PlanningPokerVote): boolean => {
    if (session.estimationMode !== 'dollars') {
      return true;
    }

    if (!currentUser) {
      return false;
    }

    if (vote.userId === currentUser.id) {
      return true;
    }

    if (currentUser.role === 'admin') {
      return true;
    }

    if (currentUser.role === 'developer') {
      return false;
    }

    return !vote.isContractor;
  };

  const canViewSessionStatistics = (session: PlanningPokerSession): boolean => {
    if (session.estimationMode !== 'dollars') {
      return true;
    }

    return currentUser?.role === 'admin';
  };

  const canCastVoteInSession = (session: PlanningPokerSession): boolean => {
    if (session.estimationMode !== 'dollars') {
      return true;
    }

    return currentUser?.role !== 'developer';
  };

  const renderSessionCard = (task: Task) => {
    const session = task.planningPokerSession;
    if (!session) return null;

    const myVoteObj = session.votes.find(v => v.userId === currentUser?.id);
    const hasVoted = !!myVoteObj;
    const allVoted = session.participantIds.length === session.votes.length;
    const consensus = getConsensus(session.votes);
    const average = calculateAverage(session.votes);
    const project = state.projects.find(p => p.id === task.projectId);
    const canSeeVotes = canSeeOtherVotes(session);
    const isCreator = session.createdBy === currentUser?.id;
    const isAdmin = currentUser?.role === 'admin' || currentUser?.role === 'stakeholder';
    const canCastVote = canCastVoteInSession(session);
    const canViewStats = canViewSessionStatistics(session);

    const pokerValues = session.estimationMode === 'dollars' 
      ? POKER_VALUES_DOLLARS 
      : POKER_VALUES_HOURS;

    return (
      <Card key={task.id} className="border-border bg-card text-card-foreground transition-shadow hover:shadow-md">
        <CardHeader>
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-2">
                {session.estimationMode === 'dollars' ? (
                  <DollarSign className="h-5 w-5 text-primary" />
                ) : (
                  <Clock className="h-5 w-5 text-primary" />
                )}
                <CardTitle className="text-lg">{task.title}</CardTitle>
              </div>
              <CardDescription className="line-clamp-2">{task.description}</CardDescription>
              {session.description && (
                <p className="mt-2 text-sm italic text-muted-foreground">"{session.description}"</p>
              )}
              <div className="flex items-center gap-2 mt-3 flex-wrap">
                <Badge variant="outline">{project?.name}</Badge>
                <Badge variant={task.priority === 'High' || task.priority === 'Critical' ? 'destructive' : 'secondary'}>
                  {task.priority}
                </Badge>
                {session.privateVoting && (
                  <Badge className="border border-border bg-accent/40 text-foreground">
                    <Lock className="h-3 w-3 mr-1" />
                    Private Voting
                  </Badge>
                )}
                <Badge className={session.revealed ? 'bg-primary text-primary-foreground' : 'bg-secondary text-secondary-foreground'}>
                  {session.revealed ? 'Revealed' : 'Voting'}
                </Badge>
              </div>
            </div>
          </div>
        </CardHeader>

        <CardContent className="space-y-6">
          {/* Voting status */}
          <div className={`${POKER_INSET_PANEL_CLASS} flex items-center justify-between p-3`}>
            <div className="flex items-center gap-3">
              <Users className="h-5 w-5 text-muted-foreground" />
              <span className="text-sm font-medium">
                {session.votes.length} / {session.participantIds.length} voted
              </span>
            </div>
            {allVoted && !session.revealed && (
              <Badge className="bg-primary text-primary-foreground">All votes in!</Badge>
            )}
          </div>

          {/* Voting buttons */}
          {!hasVoted && !session.revealed && (
            <div>
              <Label className="mb-3 block">Cast your vote:</Label>
              {canCastVote ? (
                <div className="grid grid-cols-5 gap-2">
                  {pokerValues.map((value) => (
                    <Button
                      key={value}
                      onClick={() => {
                        if (value === 'Custom' && session.allowCustomValues) {
                          const input = prompt('Enter custom amount:');
                          if (input && !isNaN(parseFloat(input))) {
                            setCustomAmount(input);
                            castVote(task, 'Custom');
                          }
                        } else {
                          castVote(task, value);
                        }
                      }}
                      variant="outline"
                      className="h-16 hover:border-ring hover:bg-accent/20"
                    >
                      {getVoteIcon(value, session.estimationMode)}
                    </Button>
                  ))}
                </div>
              ) : (
                <div className={`${WIDGET_ALERT_WARNING_CLASS} p-3 text-sm ${WIDGET_ALERT_WARNING_TEXT_CLASS}`}>
                  Cost values are restricted for your role.
                </div>
              )}
            </div>
          )}

          {/* My vote */}
          {hasVoted && (
            <div className="rounded-lg border-2 border-primary/40 bg-primary/10 p-4">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-foreground">Your vote:</span>
                <div className="text-2xl font-bold text-primary">
                  {myVoteObj && canViewVoteValue(session, myVoteObj)
                    ? session.estimationMode === 'dollars' && myVoteObj.vote !== '?' && myVoteObj.vote !== '☕'
                      ? `$${parseFloat(myVoteObj.vote).toLocaleString()}`
                      : myVoteObj.vote
                    : 'Hidden'}
                </div>
              </div>
            </div>
          )}

          {/* Votes display */}
          {canSeeVotes && session.votes.length > 0 && (
            <div>
              <Label className="mb-3 block">
                {session.revealed ? 'All Votes:' : 'Votes Submitted:'}
              </Label>
              <div className="space-y-2">
                {session.votes.map((vote) => (
                  <div
                    key={vote.userId}
                    className="flex items-center justify-between rounded-lg border border-border bg-card p-3"
                  >
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{vote.userName}</span>
                      {vote.isContractor && (
                        <Badge variant="outline" className="text-xs">Contractor</Badge>
                      )}
                    </div>
                    {session.revealed ? (
                      canViewVoteValue(session, vote) ? (
                        <span className="font-bold text-lg">
                          {session.estimationMode === 'dollars' && vote.vote !== '?' && vote.vote !== '☕'
                            ? `$${parseFloat(vote.vote).toLocaleString()}`
                            : vote.vote}
                        </span>
                      ) : (
                        <span className="text-xs text-muted-foreground">Hidden (admin only)</span>
                      )
                    ) : (
                      <CheckCircle className="h-5 w-5 text-primary" />
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Private voting message */}
          {session.privateVoting && !session.revealed && !canSeeVotes && (
            <div className={`${WIDGET_ALERT_PURPLE_CLASS} p-4`}>
              <div className="flex items-start gap-2">
                <Lock className={`mt-0.5 h-5 w-5 ${WIDGET_ALERT_PURPLE_TEXT_CLASS}`} />
                <div className={`text-sm ${WIDGET_ALERT_PURPLE_TEXT_CLASS}`}>
                  <p className="font-medium mb-1">Private Bidding Mode</p>
                  <p>Other contractors cannot see your bid until the session owner reveals all votes.</p>
                </div>
              </div>
            </div>
          )}

          {/* Statistics */}
          {session.revealed && session.votes.length > 0 && (
            canViewStats ? (
              <div className="grid grid-cols-2 gap-4">
                <div className={`${POKER_INSET_PANEL_CLASS} p-4`}>
                  <div className="mb-1 text-sm text-muted-foreground">Average</div>
                  <div className="text-2xl font-bold text-foreground">
                    {session.estimationMode === 'dollars' ? '$' : ''}{average.toLocaleString()}{session.estimationMode === 'hours' ? 'h' : ''}
                  </div>
                </div>
                <div className={`${POKER_INSET_PANEL_CLASS} p-4`}>
                  <div className="mb-1 text-sm text-muted-foreground">Consensus</div>
                  <div className="text-2xl font-bold">
                    {consensus.hasConsensus ? (
                      <span className="text-primary">
                        {session.estimationMode === 'dollars' ? '$' : ''}{consensus.value}{session.estimationMode === 'hours' ? 'h' : ''}
                      </span>
                    ) : (
                      <span className={WIDGET_ALERT_WARNING_TEXT_CLASS}>No</span>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <div className={`${WIDGET_ALERT_WARNING_CLASS} p-4 text-sm ${WIDGET_ALERT_WARNING_TEXT_CLASS}`}>
                Detailed cost estimate statistics are visible to admins only.
              </div>
            )
          )}

          {/* Actions */}
          <div className="flex flex-wrap gap-2 border-t border-border pt-4">
            {(isCreator || isAdmin) && !session.revealed && allVoted && (
              <Button onClick={() => revealVotes(task)}>
                <Eye className="h-4 w-4 mr-2" />
                Reveal Votes
              </Button>
            )}

            {(isCreator || isAdmin) && session.revealed && (
              <>
                <Button
                  onClick={() => {
                    const estimate = prompt(
                      `Enter final ${session.estimationMode === 'dollars' ? 'cost' : 'estimate'} (suggested: ${average}):`,
                      average.toString()
                    );
                    if (estimate && !isNaN(parseFloat(estimate))) {
                      finalizeEstimate(task, parseFloat(estimate));
                    }
                  }}
                >
                  <CheckCircle className="h-4 w-4 mr-2" />
                  Finalize {session.estimationMode === 'dollars' ? 'Cost' : 'Estimate'}
                </Button>
                <Button onClick={() => redoVoting(task)} variant="outline">
                  <EyeOff className="h-4 w-4 mr-2" />
                  Hide & Revote
                </Button>
              </>
            )}

            {(isCreator || isAdmin) && (
              <>
                <Button onClick={() => restartVoting(task)} variant="outline">
                  <TrendingUp className="h-4 w-4 mr-2" />
                  Restart
                </Button>
                <Button onClick={() => stopSession(task)} variant="destructive">
                  Stop Session
                </Button>
              </>
            )}

            <Button onClick={() => navigate(`/task/${task.id}`)} variant="outline">
              View Task
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="mx-auto max-w-7xl px-4 pb-6 pt-3 sm:px-6 lg:px-8 lg:pt-4">
      <div className="mb-5 sm:mb-6">
        <h1 className="text-xl font-semibold tracking-tight text-foreground sm:text-2xl">Planning Poker</h1>
        <p className="mt-1 text-xs text-muted-foreground sm:text-sm">
          Collaborative estimation for tasks and contractor bidding
        </p>
      </div>

      {/* Active Sessions */}
      {tasksWithActiveSessions.length > 0 && (
        <div className="mb-5 sm:mb-6">
          <h2 className="mb-3 text-lg font-semibold text-foreground sm:mb-4 sm:text-xl">Active Sessions</h2>
          <div className="grid gap-4">
            {tasksWithActiveSessions.map(task => renderSessionCard(task))}
          </div>
        </div>
      )}

      {/* Create New Session */}
      <Card className="border-border bg-card text-card-foreground shadow-sm">
        <CardHeader>
          <CardTitle>Start New Estimation Session</CardTitle>
          <CardDescription>
            Select a task and configure estimation settings
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
            {/* Estimation Mode Selection */}
            <div>
              <Label className="mb-3 block">Estimation Mode</Label>
              <div className={`grid ${isCurrentUserDeveloper ? 'grid-cols-1' : 'grid-cols-2'} gap-4`}>
                <button
                  onClick={() => {
                    setEstimationMode('hours');
                    setPrivateVoting(false);
                  }}
                  className={`p-4 border-2 rounded-lg transition-all ${
                    estimationMode === 'hours'
                      ? 'border-ring bg-accent/30'
                      : 'border-border hover:border-ring'
                  }`}
                >
                  <Clock className="mx-auto mb-2 h-8 w-8 text-primary" />
                  <div className="font-medium">Time Estimation</div>
                  <div className="text-sm text-muted-foreground">Hours for task completion</div>
                </button>
                {!isCurrentUserDeveloper && (
                  <button
                    onClick={() => {
                      setEstimationMode('dollars');
                      setPrivateVoting(true);
                      setAllowCustomValues(true);
                      setSelectedParticipants((previous) =>
                        previous.filter((participantId) => {
                          const participant = orgUsers.find((user) => user.id === participantId);
                          return participant ? participant.role !== 'developer' : false;
                        })
                      );
                    }}
                    className={`p-4 border-2 rounded-lg transition-all ${
                      estimationMode === 'dollars'
                        ? 'border-ring bg-accent/30'
                        : 'border-border hover:border-ring'
                    }`}
                  >
                    <DollarSign className="mx-auto mb-2 h-8 w-8 text-primary" />
                    <div className="font-medium">Cost Estimation</div>
                    <div className="text-sm text-muted-foreground">Dollar bids from contractors</div>
                  </button>
                )}
              </div>
              {isCurrentUserDeveloper && (
                <p className={`mt-2 text-xs ${WIDGET_ALERT_WARNING_TEXT_CLASS}`}>
                  Cost estimation values are restricted for developer role.
                </p>
              )}
            </div>

            {/* Privacy Settings */}
            <div className="space-y-3">
              <div className={`${POKER_INSET_PANEL_CLASS} flex items-center justify-between p-4`}>
                <div className="flex items-center gap-2">
                  {privateVoting ? <Lock className={`h-5 w-5 ${WIDGET_ALERT_PURPLE_TEXT_CLASS}`} /> : <Unlock className="h-5 w-5 text-muted-foreground" />}
                  <div>
                    <div className="font-medium">Private Voting</div>
                    <div className="text-sm text-muted-foreground">
                      {estimationMode === 'dollars' 
                        ? 'Contractors cannot see each other\'s bids'
                        : 'Team members cannot see each other\'s votes'}
                    </div>
                  </div>
                </div>
                <input
                  type="checkbox"
                  title="Toggle private voting"
                  checked={privateVoting}
                  onChange={(e) => setPrivateVoting(e.target.checked)}
                  className="w-5 h-5"
                />
              </div>

              {estimationMode === 'dollars' && (
                <div className={`${POKER_INSET_PANEL_CLASS} flex items-center justify-between p-4`}>
                  <div className="flex items-center gap-3">
                    <SettingsIcon className="h-5 w-5 text-muted-foreground" />
                    <div>
                      <div className="font-medium">Allow Custom Amounts</div>
                      <div className="text-sm text-muted-foreground">
                        Let participants enter any dollar amount
                      </div>
                    </div>
                  </div>
                  <input
                    type="checkbox"
                    title="Allow custom estimation amounts"
                    checked={allowCustomValues}
                    onChange={(e) => setAllowCustomValues(e.target.checked)}
                    className="w-5 h-5"
                  />
                </div>
              )}
            </div>

            {/* Optional Description */}
            <div>
              <Label htmlFor="description">Session Description (Optional)</Label>
              <Textarea
                id="description"
                value={sessionDescription}
                onChange={(e) => setSessionDescription(e.target.value)}
                placeholder="Add context about what needs to be estimated..."
                rows={3}
                className="mt-2"
              />
            </div>

            {/* Participant Selection */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <Label>Select Participants</Label>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    if (selectedParticipants.length === participantCandidateIds.length) {
                      setSelectedParticipants([]);
                    } else {
                      setSelectedParticipants(participantCandidateIds);
                    }
                  }}
                >
                  {selectedParticipants.length === participantCandidateIds.length ? 'Deselect All' : 'Select All'}
                </Button>
              </div>
              <div className={`${POKER_INSET_PANEL_CLASS} grid max-h-48 grid-cols-2 gap-2 overflow-y-auto p-3`}>
                {participantCandidates.map(user => (
                  <label
                    key={user.id}
                    className="flex cursor-pointer items-center gap-2 rounded p-2 transition-colors hover:bg-accent/20"
                  >
                    <input
                      type="checkbox"
                      checked={selectedParticipants.includes(user.id)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedParticipants([...selectedParticipants, user.id]);
                        } else {
                          setSelectedParticipants(selectedParticipants.filter(id => id !== user.id));
                        }
                      }}
                      className="w-4 h-4"
                    />
                    <div className="flex items-center gap-2 flex-1">
                      <UserCheck className="h-4 w-4 text-muted-foreground" />
                      <span className="text-sm font-medium">{user.name}</span>
                      <Badge variant="outline" className="text-xs ml-auto">
                        {user.role}
                      </Badge>
                    </div>
                  </label>
                ))}
              </div>
              <p className="mt-2 text-sm text-muted-foreground">
                {selectedParticipants.length > 0 
                  ? `${selectedParticipants.length} participant${selectedParticipants.length !== 1 ? 's' : ''} selected` 
                  : 'No participants selected - all eligible users will be invited'}
              </p>
            </div>

            {/* Task Selection */}
            <div>
              <Label>Select Task</Label>
              <div className="mt-2 max-h-64 overflow-y-auto space-y-2">
                {tasksReadyForEstimation.length === 0 ? (
                  <p className="py-4 text-center text-muted-foreground">No tasks available for estimation</p>
                ) : (
                  tasksReadyForEstimation.map(task => {
                    const project = state.projects.find(p => p.id === task.projectId);
                    return (
                      <button
                        key={task.id}
                        onClick={() => {
                          setSelectedTask(task);
                          setShowCreateSession(true);
                        }}
                        className="w-full rounded-lg border border-border p-4 text-left transition-colors hover:border-ring hover:bg-accent/20"
                      >
                        <div className="font-medium mb-1">{task.title}</div>
                        <div className="line-clamp-1 text-sm text-muted-foreground">{task.description}</div>
                        <div className="flex gap-2 mt-2">
                          <Badge variant="outline">{project?.name}</Badge>
                          <Badge variant="secondary">{task.priority}</Badge>
                        </div>
                      </button>
                    );
                  })
                )}
              </div>
            </div>

          {showCreateSession && selectedTask && (
            <Button
              onClick={() => createSession(selectedTask)}
              className="w-full"
              size="lg"
            >
              <Zap className="h-5 w-5 mr-2" />
              Start {estimationMode === 'dollars' ? 'Cost Bidding' : 'Estimation'} Session
            </Button>
          )}
        </CardContent>
      </Card>
    </div>
  );
}